import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import translations from './translations';

const STORAGE_KEY = 'clinity_lang';
const DEFAULT_LANG = 'en';
export const SUPPORTED_LANGS = ['en', 'hi', 'mr'];

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (key) => key,
});

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && SUPPORTED_LANGS.includes(saved)) return saved;
    } catch {}
    return DEFAULT_LANG;
  });

  // Persist + update <html lang> for screen-readers/SEO
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch {}
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (SUPPORTED_LANGS.includes(next)) setLangState(next);
  }, []);

  // t(key, vars?) — falls back to English then to the raw key
  const t = useCallback((key, vars) => {
    const dict = translations[lang] || translations.en;
    let val = dict[key];
    if (val === undefined) val = translations.en[key];
    if (val === undefined) return key;
    if (vars) {
      Object.entries(vars).forEach(([k, v]) => {
        val = val.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
      });
    }
    return val;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

export function useTranslation() {
  return useContext(LanguageContext);
}
